import { useRef, useState } from 'react';
import { UploadCloud, Loader2, X, Link as LinkIcon } from 'lucide-react';
import api from '../lib/api';

/**
 * Admin image picker — uploads to S3 via /api/uploads and returns the public URL.
 * Falls back to pasting a URL manually.
 */
export default function ImageUploader({ value, onChange, folder = 'uploads', label = 'Image' }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [showUrl, setShowUrl] = useState(false);

  const pick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post(`/uploads?folder=${folder}`, form);
      onChange(data.url);
    } catch (err) {
      setError(err?.response?.data?.message || 'Upload failed. Check S3 config.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-sm text-ink-300">{label}</span>
        <button
          type="button"
          onClick={() => setShowUrl((v) => !v)}
          className="flex items-center gap-1 text-xs text-ink-400 hover:text-white"
        >
          <LinkIcon size={12} /> {showUrl ? 'Hide URL' : 'Paste URL'}
        </button>
      </div>

      {value ? (
        <div className="relative overflow-hidden rounded-lg border border-white/10">
          <img src={value} alt="preview" className="h-40 w-full object-cover" />
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-black/60 text-white hover:bg-black/80"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={pick}
          disabled={uploading}
          className="flex h-40 w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-white/15 bg-white/5 text-ink-300 transition hover:border-brand hover:text-white"
        >
          {uploading ? (
            <><Loader2 size={22} className="animate-spin" /> Uploading…</>
          ) : (
            <><UploadCloud size={22} /> <span className="text-sm">Click to upload image</span><span className="text-xs text-ink-500">PNG / JPG · any size</span></>
          )}
        </button>
      )}

      <input ref={inputRef} type="file" accept="image/*" onChange={onFile} className="hidden" />

      {showUrl && (
        <input
          type="url"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…"
          className="mt-2 w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white outline-none focus:border-brand"
        />
      )}

      {error && <p className="mt-1 text-xs text-danger">{error}</p>}
    </div>
  );
}
